import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMyRegistrations } from '../api/registrations.api';
import { getMe } from '../api/auth.api';
import { Registration } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import DeletePlayerModal from '../components/DeletePlayerModal';
import { getAdminPlayers, updatePlayerStatus, deletePlayer, Player } from '../api/admin.api';

const DashboardPage: React.FC = () => {
  const { user, status, isAdmin } = useAuth();
  const [userStatus, setUserStatus] = useState<string | null>(null);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [adminPlayers, setAdminPlayers] = useState<Player[]>([]);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]); // Store all fetched players
  const [statusFilter, setStatusFilter] = useState<string[]>([
    'PAYMENT_SUBMITTED',
    'PENDING_APPROVAL',
    'APPROVED',
    'DECLINED'
  ]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [districtFilter, setDistrictFilter] = useState<string[]>([]);
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string[]>([]);
  const [adminLoading, setAdminLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState<{show: boolean, playerId: string | null, playerName: string}>({
    show: false,
    playerId: null,
    playerName: ''
  });

  useEffect(() => {
    // Admin always has access, skip status check
    if (isAdmin) {
      const fetchRegistrations = async () => {
        try {
          const response = await getMyRegistrations();
          setRegistrations(response.data || []);
        } catch (err) {
          // Silent fail for prototype.
        } finally {
          setLoading(false);
        }
      };
      fetchRegistrations();
      return;
    }

    // Regular users: check status
    if (status === 'pending') {
      setLoading(false);
      return;
    }

    // Fetch user status from API
    const fetchUserStatus = async () => {
      try {
        const response = await getMe();
        const userStatus = (response.data as any)?.status;
        setUserStatus(userStatus);
        
        // Only fetch registrations if approved
        if (userStatus === 'APPROVED') {
          const response = await getMyRegistrations();
          setRegistrations(response.data || []);
        }
      } catch (err) {
        // Silent fail for prototype.
      } finally {
        setLoading(false);
      }
    };

    fetchUserStatus();
  }, [status, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchPlayers = async () => {
      setAdminLoading(true);
      try {
        // Only fetch if at least one filter is selected
        if (statusFilter.length > 0) {
          const response = await getAdminPlayers({ status: statusFilter });
          const players = response.data || [];
          setAllPlayers(players); // Store all fetched players
          setAdminPlayers(players); // Initially show all
        } else {
          setAllPlayers([]);
          setAdminPlayers([]);
        }
      } catch (err) {
        setAllPlayers([]);
        setAdminPlayers([]);
      } finally {
        setAdminLoading(false);
      }
    };
    fetchPlayers();
  }, [isAdmin, statusFilter]);

  // Client-side filtering for search, district, and payment method
  useEffect(() => {
    if (!isAdmin) return;
    
    let filtered = [...allPlayers];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(player =>
        player.name.toLowerCase().includes(query) ||
        player.phone?.toLowerCase().includes(query) ||
        player.email?.toLowerCase().includes(query) ||
        player.transaction_id?.toLowerCase().includes(query) ||
        player.district_name?.toLowerCase().includes(query) ||
        player.district_code?.toLowerCase().includes(query) ||
        player.player_id?.toLowerCase().includes(query) ||
        player.playerId?.toLowerCase().includes(query)
      );
    }

    // Apply district filter
    if (districtFilter.length > 0) {
      filtered = filtered.filter(player =>
        districtFilter.includes(player.district_code || '')
      );
    }

    // Apply payment method filter
    if (paymentMethodFilter.length > 0) {
      filtered = filtered.filter(player =>
        paymentMethodFilter.includes(player.payment_method || 'N/A')
      );
    }

    setAdminPlayers(filtered);
  }, [isAdmin, allPlayers, searchQuery, districtFilter, paymentMethodFilter]);

  const toggleStatusFilter = (status: string) => {
    setStatusFilter((prev) =>
      prev.includes(status) ? prev.filter((item) => item !== status) : [...prev, status]
    );
  };

  const toggleDistrictFilter = (district: string) => {
    setDistrictFilter((prev) =>
      prev.includes(district) ? prev.filter((item) => item !== district) : [...prev, district]
    );
  };

  const togglePaymentMethodFilter = (method: string) => {
    setPaymentMethodFilter((prev) =>
      prev.includes(method) ? prev.filter((item) => item !== method) : [...prev, method]
    );
  };

  const handleStatusChange = async (playerId: string, newStatus: 'APPROVED' | 'DECLINED' | 'PENDING_APPROVAL') => {
    try {
      await updatePlayerStatus(playerId, { status: newStatus });
      // Update local state in both arrays
      const updatePlayer = (p: Player) => p.id === playerId ? { ...p, status: newStatus } : p;
      setAllPlayers(prev => prev.map(updatePlayer));
      setAdminPlayers(prev => prev.map(updatePlayer));
    } catch (err) {
      // Silent fail for prototype
    }
  };

  const handleDeletePlayer = (playerId: string, playerName: string) => {
    setDeleteModal({
      show: true,
      playerId,
      playerName
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.playerId) return;

    try {
      await deletePlayer(deleteModal.playerId);
      // Remove player from both arrays
      setAllPlayers(prev => prev.filter(p => p.id !== deleteModal.playerId));
      setAdminPlayers(prev => prev.filter(p => p.id !== deleteModal.playerId));
      // Close modal
      setDeleteModal({ show: false, playerId: null, playerName: '' });
    } catch (err: any) {
      console.error('Delete player error:', err);
      let errorMessage = 'Failed to delete player. Please try again.';
      
      if (err.response?.status === 404) {
        errorMessage = 'Player not found. It may have already been deleted.';
      } else if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to delete players.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Please log in again to continue.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      alert(errorMessage);
      // Keep modal open on error so user can try again
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ show: false, playerId: null, playerName: '' });
  };

  const getPlayerStatusLabel = (value: string) => value.replace('_', ' ');

  // Get unique districts and payment methods from fetched data
  const uniqueDistricts = useMemo(() => {
    const districts = new Map<string, string>();
    allPlayers.forEach(player => {
      if (player.district_code && player.district_name) {
        districts.set(player.district_code, player.district_name);
      }
    });
    return Array.from(districts.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [allPlayers]);

  const uniquePaymentMethods = useMemo(() => {
    const methods = new Set<string>();
    allPlayers.forEach(player => {
      const method = player.payment_method || 'N/A';
      if (method) methods.add(method);
    });
    return Array.from(methods).sort();
  }, [allPlayers]);

  const clearAllFilters = () => {
    setSearchQuery('');
    setDistrictFilter([]);
    setPaymentMethodFilter([]);
  };

  const hasActiveFilters = searchQuery.trim() !== '' || districtFilter.length > 0 || paymentMethodFilter.length > 0;

  // Show blocking screen for non-approved regular users
  if (!isAdmin && (status === 'pending' || (userStatus && userStatus !== 'APPROVED'))) {
    return (
      <div className="container py-5 mb-5">
        <div className="card card-surface">
          <div className="card-body text-center py-5">
            <h1 className="h4 mb-3 text-white">Pending Approval</h1>
            <p className="text-muted-soft mb-0">
              Your registration is under review.
              <br />
              Please wait for admin confirmation.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'approved' || statusLower === 'success') {
      return 'bg-success';
    }
    if (statusLower === 'rejected' || statusLower === 'failed') {
      return 'bg-danger';
    }
    return 'bg-warning text-white';
  };

  if (isAdmin) {
    return (
      <div className="container py-5 mb-5">
        <div className="mb-4">
          <h1 className="h3 section-title mb-2">Admin Dashboard</h1>
          <p className="text-muted-soft mb-0">All registered players</p>
        </div>

        {/* Compact Admin Action Cards */}
        <div className="row g-3 g-md-4 mb-4">
          <div className="col-12 col-md-6 col-lg-3">
            <Link to="/dashboard/admin/players" className="admin-action-card">
              <div className="admin-card-icon bg-success">
                <i className="bi bi-people"></i>
              </div>
              <div className="admin-card-content">
                <h5 className="admin-card-title">Manage Players</h5>
                <p className="admin-card-desc">View and manage all registered players</p>
              </div>
              <div className="admin-card-arrow">
                <i className="bi bi-arrow-right"></i>
              </div>
            </Link>
          </div>
          <div className="col-12 col-md-6 col-lg-3">
            <Link to="/dashboard/admin/products" className="admin-action-card">
              <div className="admin-card-icon bg-info">
                <i className="bi bi-box-seam"></i>
              </div>
              <div className="admin-card-content">
                <h5 className="admin-card-title">Manage Products</h5>
                <p className="admin-card-desc">Add, edit, and manage products</p>
              </div>
              <div className="admin-card-arrow">
                <i className="bi bi-arrow-right"></i>
              </div>
            </Link>
          </div>
          <div className="col-12 col-md-6 col-lg-3">
            <Link to="/dashboard/admin/categories" className="admin-action-card">
              <div className="admin-card-icon bg-warning">
                <i className="bi bi-tags"></i>
              </div>
              <div className="admin-card-content">
                <h5 className="admin-card-title">Manage Categories</h5>
                <p className="admin-card-desc">Organize product categories</p>
              </div>
              <div className="admin-card-arrow">
                <i className="bi bi-arrow-right"></i>
              </div>
            </Link>
          </div>
          <div className="col-12 col-md-6 col-lg-3">
            <Link to="/dashboard/admin/notices" className="admin-action-card">
              <div className="admin-card-icon bg-danger">
                <i className="bi bi-megaphone"></i>
              </div>
              <div className="admin-card-content">
                <h5 className="admin-card-title">Manage Notices</h5>
                <p className="admin-card-desc">Create and manage announcements</p>
              </div>
              <div className="admin-card-arrow">
                <i className="bi bi-arrow-right"></i>
              </div>
            </Link>
          </div>
          <div className="col-12 col-md-6 col-lg-3">
            <Link to="/dashboard/admin/events" className="admin-action-card">
              <div className="admin-card-icon bg-info">
                <i className="bi bi-calendar-event"></i>
              </div>
              <div className="admin-card-content">
                <h5 className="admin-card-title">Manage Events</h5>
                <p className="admin-card-desc">Create, edit, and manage events</p>
              </div>
              <div className="admin-card-arrow">
                <i className="bi bi-arrow-right"></i>
              </div>
            </Link>
          </div>
          <div className="col-12 col-md-6 col-lg-3">
            <Link to="/dashboard/admin/gallery" className="admin-action-card">
              <div className="admin-card-icon bg-primary">
                <i className="bi bi-images"></i>
              </div>
              <div className="admin-card-content">
                <h5 className="admin-card-title">Manage Gallery</h5>
                <p className="admin-card-desc">Upload, edit, and manage gallery images</p>
              </div>
              <div className="admin-card-arrow">
                <i className="bi bi-arrow-right"></i>
              </div>
            </Link>
          </div>
          <div className="col-12 col-md-6 col-lg-3">
            <Link to="/dashboard/admin/programs" className="admin-action-card">
              <div className="admin-card-icon bg-secondary">
                <i className="bi bi-briefcase"></i>
              </div>
              <div className="admin-card-content">
                <h5 className="admin-card-title">Manage Programs</h5>
                <p className="admin-card-desc">Manage Hunting & Dealership Program categories</p>
              </div>
              <div className="admin-card-arrow">
                <i className="bi bi-arrow-right"></i>
              </div>
            </Link>
          </div>
        </div>

        {/* Search Bar */}
        <div className="card card-surface mb-4">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-8">
                <label htmlFor="playerSearch" className="form-label fw-semibold mb-2">Live Search</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    id="playerSearch"
                    placeholder="Search by name, phone, email, transaction ID, or district..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoFocus
                  />
                  {searchQuery && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setSearchQuery('')}
                      title="Clear search"
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-4 mt-3 mt-md-0">
                <div className="text-md-end text-start">
                  <div className="small text-muted-soft mb-1">Results</div>
                  <div className="h5 mb-0">
                    <span className="text-white">{adminPlayers.length}</span>
                    <span className="text-muted-soft"> / {allPlayers.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Section with Checkboxes */}
        <div className="card card-surface mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Filters</h5>
              {hasActiveFilters && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={clearAllFilters}
                >
                  Clear Filters
                </button>
              )}
            </div>

            {/* Status Filter */}
            <div className="mb-4">
              <label className="form-label fw-semibold mb-2 d-block">Status:</label>
              <div className="row g-2 g-md-3">
                {['PAYMENT_SUBMITTED', 'PENDING_APPROVAL', 'APPROVED', 'DECLINED'].map((statusValue) => (
                  <div key={statusValue} className="col-6 col-md-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`filter-status-${statusValue}`}
                        checked={statusFilter.includes(statusValue)}
                        onChange={() => toggleStatusFilter(statusValue)}
                      />
                      <label className="form-check-label small" htmlFor={`filter-status-${statusValue}`}>
                        {getPlayerStatusLabel(statusValue)}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* District Filter */}
            {uniqueDistricts.length > 0 && (
              <div className="mb-4">
                <label className="form-label fw-semibold mb-2 d-block">District:</label>
                <div className="row g-2">
                  {uniqueDistricts.slice(0, 8).map(([code, name]) => (
                    <div key={code} className="col-6 col-md-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`filter-district-${code}`}
                          checked={districtFilter.includes(code)}
                          onChange={() => toggleDistrictFilter(code)}
                        />
                        <label className="form-check-label" htmlFor={`filter-district-${code}`}>
                          {name}
                        </label>
                      </div>
                    </div>
                  ))}
                  {uniqueDistricts.length > 8 && (
                    <div className="col-12">
                      <small className="text-muted-soft">
                        Showing 8 of {uniqueDistricts.length} districts. Use search to find specific districts.
                      </small>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payment Method Filter */}
            {uniquePaymentMethods.length > 0 && (
              <div>
                <label className="form-label fw-semibold mb-2 d-block">Payment Method:</label>
                <div className="row g-3">
                  {uniquePaymentMethods.map((method) => (
                    <div key={method} className="col-6 col-md-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`filter-payment-${method}`}
                          checked={paymentMethodFilter.includes(method)}
                          onChange={() => togglePaymentMethodFilter(method)}
                        />
                        <label className="form-check-label" htmlFor={`filter-payment-${method}`}>
                          {method}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {statusFilter.length === 0 && (
              <div className="alert alert-warning mb-0 mt-3">
                Please select at least one status to view players.
              </div>
            )}
          </div>
        </div>

        {/* Players Table */}
        <div className="card card-surface">
          <div className="card-body">
            {adminLoading ? (
              <LoadingSpinner />
            ) : statusFilter.length === 0 ? (
              <div className="alert alert-warning mb-0">
                Please select at least one status filter to view players.
              </div>
            ) : adminPlayers.length === 0 ? (
              <div className="alert alert-secondary mb-0">
                {hasActiveFilters
                  ? 'No players found matching your search and filter criteria.'
                  : 'No players found for the selected status(es).'}
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="table-responsive d-none d-md-block">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Player ID</th>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>District</th>
                        <th>Payment Method</th>
                        <th>Transaction ID</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminPlayers.map((player) => (
                        <tr key={player.id}>
                          <td>
                            {player.player_id || player.playerId ? (
                              <span className="badge bg-success text-white fw-bold">
                                {player.player_id || player.playerId}
                              </span>
                            ) : (
                              <span className="text-muted-soft small">N/A</span>
                            )}
                          </td>
                          <td className="fw-semibold">{player.name}</td>
                          <td>{player.phone || 'N/A'}</td>
                          <td>{player.district_name || player.district_code || 'N/A'}</td>
                          <td>{player.payment_method || 'N/A'}</td>
                          <td>
                            {player.transaction_id ? (
                              <code className="small">{player.transaction_id}</code>
                            ) : (
                              'N/A'
                            )}
                          </td>
                          <td>
                            <span className={`badge ${getStatusBadge(player.status)} badge-status`}>
                              {player.status.replace('_', ' ')}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex gap-1 flex-wrap">
                              <button
                                type="button"
                                className="btn btn-sm btn-success"
                                onClick={() => handleStatusChange(player.id, 'APPROVED')}
                                disabled={player.status === 'APPROVED'}
                                title="Approve"
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => handleStatusChange(player.id, 'DECLINED')}
                                disabled={player.status === 'DECLINED'}
                                title="Decline"
                              >
                                Decline
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-warning"
                                onClick={() => handleStatusChange(player.id, 'PENDING_APPROVAL')}
                                disabled={player.status === 'PENDING_APPROVAL'}
                                title="Set Pending"
                              >
                                Pending
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeletePlayer(player.id, player.name)}
                                title="Delete Player"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="d-md-none">
                  {adminPlayers.map((player) => (
                    <div key={player.id} className="card card-surface mb-3">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <h6 className="mb-0 fw-bold">{player.name}</h6>
                              {player.player_id || player.playerId ? (
                                <span className="badge bg-success text-white" style={{ fontSize: '0.7rem' }}>
                                  {player.player_id || player.playerId}
                                </span>
                              ) : null}
                            </div>
                            <div className="small text-muted-soft mb-1">
                              <i className="bi bi-telephone me-1"></i>
                              {player.phone || 'N/A'}
                            </div>
                            <div className="small text-muted-soft mb-1">
                              <i className="bi bi-geo-alt me-1"></i>
                              {player.district_name || player.district_code || 'N/A'}
                            </div>
                            <div className="small text-muted-soft mb-1">
                              <i className="bi bi-credit-card me-1"></i>
                              {player.payment_method || 'N/A'}
                            </div>
                            {player.transaction_id && (
                              <div className="small text-muted-soft mb-2">
                                <i className="bi bi-receipt me-1"></i>
                                <code className="small">{player.transaction_id}</code>
                              </div>
                            )}
                          </div>
                          <span className={`badge ${getStatusBadge(player.status)} badge-status`}>
                            {player.status.replace('_', ' ')}
                          </span>
                        </div>
                        <div className="d-grid gap-2 mt-3">
                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            onClick={() => handleStatusChange(player.id, 'APPROVED')}
                            disabled={player.status === 'APPROVED'}
                          >
                            <i className="bi bi-check-circle me-1"></i>
                            Approve
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleStatusChange(player.id, 'DECLINED')}
                            disabled={player.status === 'DECLINED'}
                          >
                            <i className="bi bi-x-circle me-1"></i>
                            Decline
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-warning"
                            onClick={() => handleStatusChange(player.id, 'PENDING_APPROVAL')}
                            disabled={player.status === 'PENDING_APPROVAL'}
                          >
                            <i className="bi bi-clock me-1"></i>
                            Set Pending
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeletePlayer(player.id, player.name)}
                          >
                            <i className="bi bi-trash me-1"></i>
                            Delete Player
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Delete Player Confirmation Modal */}
        <DeletePlayerModal
          show={deleteModal.show}
          playerName={deleteModal.playerName}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />

        {/* Settings Section for Admin */}
        <div className="row g-4 mt-4">
          <div className="col-lg-4">
            <div className="card card-surface h-100">
              <div className="card-body">
                <h4 className="mb-3">Settings</h4>
                <div className="d-flex flex-column gap-2">
                  <Link to="/dashboard/change-password" className="btn btn-outline-secondary w-100">
                    <i className="bi bi-key me-2"></i>
                    Change Password
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5 mb-5">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
        <div>
          <h1 className="h3 section-title">Dashboard</h1>
          <p className="text-muted-soft mb-0">Welcome back, {user?.name || 'Coach'}.</p>
        </div>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-lg-7">
          <div className="card card-surface h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">My Registrations</h4>
                <Link to="/dashboard/registrations" className="text-warning text-decoration-none">
                  Manage
                </Link>
              </div>
              {loading ? (
                <LoadingSpinner />
              ) : registrations.length === 0 ? (
                <div className="alert alert-secondary mb-0">
                  No registrations yet.
                </div>
              ) : (
                <div className="list-group">
                  {registrations.slice(0, 3).map((reg) => (
                    <div key={reg.id} className="list-group-item">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-1">{reg.teamName}</h6>
                          <small className="text-muted">
                            {reg.players?.length || 0} players •{' '}
                            {reg.createdAt
                              ? new Date(reg.createdAt).toLocaleDateString()
                              : 'N/A'}
                          </small>
                        </div>
                        <span className={`badge ${getStatusBadge(reg.status)} badge-status`}>
                          {reg.status}
                        </span>
                      </div>
                    </div>
                  ))}
                  {registrations.length > 3 && (
                    <Link
                      to="/dashboard/registrations"
                      className="list-group-item text-center text-decoration-none"
                    >
                      View all {registrations.length} registrations →
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-lg-5">
          <div className="card card-surface h-100">
            <div className="card-body">
              <h4 className="mb-3">ID Cards</h4>
              <p className="text-muted-soft">
                Access team and player ID cards to speed up check-ins.
              </p>
              <Link to="/dashboard/idcards" className="btn btn-outline-secondary w-100">
                View ID Cards
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
